import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { Outlet, useParams, useOutletContext, Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
  Alert,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  IconButton,
  Popover,
} from '@c2fo/liquidity';
import {
  CalendarClockIcon,
  CaretDownSolidIcon,
  ChevronDownIcon,
  InfoCircleIcon,
  QuestionCircleIcon,
} from '@c2fo/liquidity/icons';
import BuyerLogo from '@/enterprise/components/BuyerLogo';
import EmptyRender from '@/enterprise/components/EmptyRender';
import NextMarketClearTimeCountdown from '@/enterprise/components/NextMarketClearTimeCountdown';
import Skeleton from '@/enterprise/components/Skeleton';
import useGroupByBuyer, { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import useTakerMarkets, { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { IconButtonLink } from '@/shared/components/IconButtonLink';
import LinkTabs from '@/shared/components/LinkTabs';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useLocaleFormat from '@/utils/useLocaleFormat';
import usePricingTypeDisplay from '@/utils/usePricingTypeDisplay';
import { BuyerNavigationLayoutError } from './BuyerNavigationLayoutError';

export interface BuyerNavigationLayoutContext {
  // current selected buyer
  readonly buyer: BuyerGroupWithStats;
  // loader for the current selected buyer
  readonly loader: {
    loadingKey: number;
    setLoadingKey: Dispatch<SetStateAction<number>>;
  };
  // current selected taker market
  readonly takerMarket: TakerMarket;
  // url params for the current selected buyer and taker market
  readonly urlParams: {
    makerOrganizationUuid: string;
    marketId: string;
    takerId: number;
  };
}

export function useBuyerNavigationLayoutContext() {
  return useOutletContext<BuyerNavigationLayoutContext>();
}

const BuyerNavigationLayoutComponent = () => {
  const { t } = useTranslation();
  const { asCurrency, asDateStringText } = useLocaleFormat();
  const [loadingKey, setLoadingKey] = useState<number>(0);
  const { getPricingTypeDisplay } = usePricingTypeDisplay();
  const params = useParams<{ makerOrganizationUuid: string; marketId: string; takerId: string }>();
  const { makerOrganizationUuid = '', marketId = '', takerId: takerIdParam = '' } = params;
  const takerId = parseInt(takerIdParam);

  const { data: { takerMarket, takerMarkets } = { takerMarket: undefined, takerMarkets: [] } } = useTakerMarkets(
    useCallback(
      (takerMarkets: TakerMarket[]) => {
        if (isNaN(takerId)) {
          return;
        }

        const takerMarket = takerMarkets.find(
          (tm) =>
            tm.makerOrganizationUuid === makerOrganizationUuid &&
            tm.marketId === marketId &&
            tm.takerDivisionId === takerId
        );

        return { takerMarket, takerMarkets };
      },
      [makerOrganizationUuid, marketId, takerId]
    )
  );
  const buyers = useGroupByBuyer({ takerMarkets });

  if (!makerOrganizationUuid || !marketId || !takerId || !takerMarket || buyers.length === 0) {
    return (
      <BuyerNavigationLayoutError marketId={marketId} takerId={takerId} makerOrganizationUuid={makerOrganizationUuid} />
    );
  }

  const currentBuyerIndex = buyers.findIndex((buyer) => buyer.makerOrganizationUuid === makerOrganizationUuid);
  const currentBuyer = buyers[currentBuyerIndex];
  const nextBuyer = buyers[currentBuyerIndex + 1];
  const previousBuyer = buyers[currentBuyerIndex - 1];

  // Dates for communicating to the user the next estimated deposit date for selected market
  const nextPaymentDate = takerMarket.marketGoodFundsDate;
  const paymentDateLabel = t('draftOffer.labels.estimatedDepositDate');
  const paymentPopoverContent = t('taker.nextDepositDatePopover.content');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-6 overflow-hidden pb-4">
        <div className="flex items-center gap-2 overflow-hidden">
          <div>
            <IconButtonLink
              className="rotate-90"
              disabled={!previousBuyer}
              icon={ChevronDownIcon}
              name="previous"
              to={asEnterpriseLink(
                `customers/${previousBuyer?.makerOrganizationUuid}/markets/${previousBuyer?.takerMarkets[0].marketId}/division/${previousBuyer?.takerMarkets[0].takerDivisionId}/invoices/eligible`
              )}
              variant="ancillary"
            />
          </div>
          <div className="flex items-center gap-2 overflow-hidden">
            <BuyerLogo
              buyerLogoUrl={currentBuyer.makerOrganizationLogo}
              buyerName={currentBuyer.makerOrganizationName}
              className="hidden lg:flex"
              size="lg"
            />
            <div className="min-w-0 font-serif text-3xl lg:text-4xl">
              <div className="-mb-1 truncate pb-1">
                <Trans
                  i18nKey="core.customerInvoices"
                  components={{ style: <span className="hidden lg:inline-block" /> }}
                  values={{ customerName: currentBuyer.makerOrganizationName }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 overflow-hidden">
          {nextBuyer && (
            <div className="truncate text-wrap text-sm text-text-secondary lg:text-xl">
              <Trans
                i18nKey="core.customerInvoices"
                components={{ style: <span className="hidden lg:inline-block" /> }}
                values={{ customerName: nextBuyer.makerOrganizationName }}
              />
            </div>
          )}
          <div>
            <IconButtonLink
              className="-rotate-90"
              disabled={!nextBuyer}
              icon={ChevronDownIcon}
              name="next"
              to={asEnterpriseLink(
                `customers/${nextBuyer?.makerOrganizationUuid}/markets/${nextBuyer?.takerMarkets[0].marketId}/division/${nextBuyer?.takerMarkets[0].takerDivisionId}/invoices/eligible`
              )}
              variant="ancillary"
            />
          </div>
        </div>
      </div>
      <div className="rounded-xl bg-white shadow-md">
        <div className="p-4">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <div className="overflow-hidden">
              <div className="flex items-center gap-2">
                <div className="max-w-md truncate text-3xl font-bold">{getTakerMarketName(takerMarket)}</div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton icon={CaretDownSolidIcon} name="Division Select Dropdown" variant="ancillary" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    avoidCollisions={false}
                    className="max-h-64 w-64 overflow-auto"
                    collisionPadding={24}
                  >
                    <DropdownMenuLabel>{t('core.viewBy')}:</DropdownMenuLabel>
                    {currentBuyer.takerMarkets.map((buyerTakerMarket) => (
                      <DropdownMenuItem
                        key={buyerTakerMarket.id}
                        asChild
                        className="overflow-hidden truncate"
                        selected={buyerTakerMarket.id === takerMarket.id}
                      >
                        <Link
                          className="inline-grid w-full truncate"
                          to={asEnterpriseLink(
                            `customers/${buyerTakerMarket.makerOrganizationUuid}/markets/${buyerTakerMarket.marketId}/division/${buyerTakerMarket.takerDivisionId}/invoices/eligible`
                          )}
                          title={`${getTakerMarketName(buyerTakerMarket)} ⋅ ${getTakerMarketDivisionTitle(
                            buyerTakerMarket
                          )?.title}`}
                        >
                          <span className="w-full truncate font-medium">{getTakerMarketName(buyerTakerMarket)}</span>
                          <span className="w-full truncate text-sm text-text-secondary">
                            {getTakerMarketDivisionTitle(buyerTakerMarket)?.content}
                          </span>
                          <span className="w-full truncate text-sm text-text-secondary">
                            {asCurrency(
                              buyerTakerMarket.eligibleInvoiceAmount + buyerTakerMarket.awardedInvoiceAmount,
                              buyerTakerMarket.currency
                            )}
                          </span>
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center gap-1 text-text-secondary">
                <div>{takerMarket.currency}</div>
                <div>|</div>
                {getPricingTypeDisplay(takerMarket)}
              </div>
            </div>
            <div className="flex flex-col space-y-1.5 lg:items-end">
              <div className="flex flex-col text-sm lg:items-end">
                <NextMarketClearTimeCountdown takerMarket={takerMarket} />
                <div className="flex items-center gap-1">
                  <CalendarClockIcon className="h-4 w-4 fill-green-700" />
                  {paymentDateLabel}:{' '}
                  <EmptyRender condition={!!nextPaymentDate}>
                    <span>{nextPaymentDate && asDateStringText({ date: nextPaymentDate })}</span>
                    <Popover>
                      <Popover.Trigger className="flex items-center gap-1" aria-label={paymentDateLabel}>
                        <QuestionCircleIcon className="fill-text-secondary" aria-hidden="true" />
                      </Popover.Trigger>
                      <Popover.Content arrow collisionPadding={24}>
                        <div className="w-72 space-y-2 text-center text-sm">
                          <div>{paymentPopoverContent}</div>
                        </div>
                      </Popover.Content>
                    </Popover>
                  </EmptyRender>
                </div>
                {takerMarket.marketIsDSF && (
                  <div className="flex items-center gap-1">
                    {t('taker.history.fundedByBankPartner')}
                    <Popover>
                      <Popover.Trigger>
                        <InfoCircleIcon className="visible fill-text-secondary" />
                      </Popover.Trigger>
                      <Popover.Content arrow side="bottom" collisionPadding={24}>
                        <div className="w-52 text-center text-sm">{t('taker.history.fundedByBankPartnerTooltip')}</div>
                      </Popover.Content>
                    </Popover>
                  </div>
                )}
                {!!takerMarket.disableAfterMarketClearsDate &&
                  takerMarket?.offerConfig.offerType !== 'PREFERRED_TERM' && (
                    <div className="flex items-center gap-1">
                      {t('taker.dictionary.expirationDate.label')}:{' '}
                      <span>{asDateStringText({ date: takerMarket.disableAfterMarketClearsDate, isUTC: false })}</span>
                    </div>
                  )}
              </div>
            </div>
          </div>
          {!takerMarket.marketIsEnabled && (
            <div className="pt-8">
              <Alert
                description={t('core.marketClosedDescription')}
                full
                title={t('core.marketClosed')}
                type="warning"
                variant="outlined"
              />
            </div>
          )}
        </div>
        <div className="border-b border-deepGray-500/[12%] px-4">
          <LinkTabs
            items={[
              {
                key: 'invoices',
                label: t('maker.invoices'),
                link: asEnterpriseLink(
                  `customers/${makerOrganizationUuid}/markets/${marketId}/division/${takerId}/invoices/eligible`
                ),
                matcher: 'invoices',
              },
              {
                key: 'history',
                label: t('core.history'),
                link: asEnterpriseLink(
                  `customers/${makerOrganizationUuid}/markets/${marketId}/division/${takerId}/history`
                ),
                matcher: 'history',
              },
            ]}
            variant="secondary"
          />
        </div>
        <Outlet
          context={
            {
              buyer: currentBuyer,
              loader: {
                loadingKey,
                setLoadingKey,
              },
              takerMarket,
              urlParams: { makerOrganizationUuid, marketId, takerId },
            } satisfies BuyerNavigationLayoutContext
          }
        />
      </div>
    </div>
  );
};

const BuyerNavigationLayout = () => {
  return (
    <QueryBoundaries
      LoadingComponent={() => (
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-6">
            <Skeleton className="h-14 w-1/2 lg:w-1/3" />
            <Skeleton className="h-14 w-1/2 lg:w-1/3" />
          </div>
          <div className="space-y-6 rounded-md bg-gray-100 p-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-80 w-full" />
          </div>
        </div>
      )}
    >
      <BuyerNavigationLayoutComponent />
    </QueryBoundaries>
  );
};

export default BuyerNavigationLayout;
